import React, { useState, useEffect } from 'react';
import { Phone, MessageCircle, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { businessInfo } from '../mockData';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCall = () => {
    window.location.href = `tel:${businessInfo.phone}`;
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${businessInfo.whatsapp}?text=Hi, I'm interested in your marble polishing services`, '_blank');
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      <header data-testid="header" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg py-3' : 'bg-white/95 backdrop-blur-sm py-4'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">GS</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">G.S Contractors</h1>
                <p className="text-xs text-emerald-600">Marble & Granite Experts</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              <button onClick={() => scrollToSection('services')} data-testid="header-nav-services" className="text-gray-700 hover:text-emerald-600 transition-colors font-medium">
                Services
              </button>
              <button onClick={() => scrollToSection('projects')} data-testid="header-nav-projects" className="text-gray-700 hover:text-emerald-600 transition-colors font-medium">
                Projects
              </button>
              <button onClick={() => scrollToSection('why-us')} data-testid="header-nav-why-us" className="text-gray-700 hover:text-emerald-600 transition-colors font-medium">
                Why Us
              </button>
              <button onClick={() => scrollToSection('testimonials')} data-testid="header-nav-testimonials" className="text-gray-700 hover:text-emerald-600 transition-colors font-medium">
                Testimonials
              </button>
              <button onClick={() => scrollToSection('contact')} data-testid="header-nav-contact" className="text-gray-700 hover:text-emerald-600 transition-colors font-medium">
                Contact
              </button>
            </nav>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center space-x-3">
              <Button 
                onClick={handleCall}
                variant="outline"
                data-testid="header-call-button"
                className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
              >
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </Button>
              <Button 
                onClick={handleWhatsApp}
                data-testid="header-whatsapp-button"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="header-mobile-menu-button"
              className="md:hidden text-gray-700"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4" data-testid="header-mobile-menu">
              <nav className="flex flex-col space-y-3">
                <button onClick={() => scrollToSection('services')} data-testid="header-mobile-nav-services" className="text-gray-700 hover:text-emerald-600 transition-colors font-medium text-left">
                  Services
                </button>
                <button onClick={() => scrollToSection('projects')} data-testid="header-mobile-nav-projects" className="text-gray-700 hover:text-emerald-600 transition-colors font-medium text-left">
                  Projects
                </button>
                <button onClick={() => scrollToSection('why-us')} data-testid="header-mobile-nav-why-us" className="text-gray-700 hover:text-emerald-600 transition-colors font-medium text-left">
                  Why Us
                </button>
                <button onClick={() => scrollToSection('testimonials')} data-testid="header-mobile-nav-testimonials" className="text-gray-700 hover:text-emerald-600 transition-colors font-medium text-left">
                  Testimonials
                </button>
                <button onClick={() => scrollToSection('contact')} data-testid="header-mobile-nav-contact" className="text-gray-700 hover:text-emerald-600 transition-colors font-medium text-left">
                  Contact
                </button>
                <div className="flex flex-col space-y-2 pt-2">
                  <Button 
                    onClick={handleCall}
                    variant="outline"
                    data-testid="header-mobile-call-button"
                    className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 w-full"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Call Now
                  </Button>
                  <Button 
                    onClick={handleWhatsApp}
                    data-testid="header-mobile-whatsapp-button"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white w-full"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp
                  </Button>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>
      {/* Spacer for fixed header */}
      <div className="h-20"></div>
    </>
  );
};

export default Header;
